import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { ArrowRight, BookOpen, ChevronDown, Compass, Download, Sparkles } from "lucide-react";
import { SmartImage } from "@/components/SmartImage";
import { useLocale } from "@/hooks/use-locale";
import { curatedUiTranslation } from "@/lib/curated-ui-translations";

interface ExploreCTA {
  href: { to: "/discovery/$slug"; params: { slug: string } } | { to: "/category/$slug"; params: { slug: string } };
  label: string;
  title: string;
}

interface Props {
  body: string;
  /** Optional mid-article exploration nudge inserted after ~40% of the paragraphs. */
  explore?: ExploreCTA;
  /** Optional themed image placed mid-article as a visual pause. */
  midImageUrl?: string | null;
  midImageAlt?: string;
  /** Optional belief gate inserted mid-article. */
  gate?: React.ReactNode;
  /** When true, everything after the gate is blurred until the reader commits. */
  locked?: boolean;
}

/**
 * Renders article body as discrete paragraphs with a narrower reading column,
 * generous leading, a drop-cap on the opener, a mid-article visual pause,
 * an optional exploration CTA, and a warm newsletter/community invitation
 * at the very end of every article.
 */
export function ArticleBody({ body, explore, midImageUrl, midImageAlt }: Props) {
  const [lang] = useLocale();
  const ui = (source: string) => curatedUiTranslation(lang, source) ?? source;
  const paragraphs = body
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  const [open, setOpen] = useState(true);

  if (paragraphs.length === 0) return null;

  const words = body.trim().split(/\s+/).length;
  const mins = Math.max(1, Math.round(words / 220));

  // Insert CTA roughly 40% through - but not before paragraph 2 or after the penultimate.
  const ctaIndex = explore && paragraphs.length >= 4
    ? Math.min(paragraphs.length - 2, Math.max(2, Math.round(paragraphs.length * 0.4)))
    : -1;
  // Insert the mid-article image roughly halfway through so it doesn't collide
  // with the CTA - never before paragraph 2, never after the penultimate.
  const imageIndex = midImageUrl && paragraphs.length >= 3
    ? Math.min(paragraphs.length - 2, Math.max(2, Math.round(paragraphs.length * 0.55)))
    : -1;

  return (
    <div className="mx-auto mt-8 max-w-[68ch] text-foreground">
      {/* The written story is the primary content and is visible by default. */}
      <div className="flex flex-wrap items-center gap-2 print:hidden">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="written-article"
          className="inline-flex flex-1 items-center justify-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-5 py-3 text-xs font-bold uppercase tracking-[0.2em] text-primary transition hover:bg-primary/20"
        >
          <BookOpen className="h-4 w-4" />
          {open ? ui("Hide the written article") : ui(`Read the written article · ${mins} min`)}
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        <button
          type="button"
          onClick={() => typeof window !== "undefined" && window.print()}
          className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-3 text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground transition hover:border-foreground hover:text-foreground"
          title={ui("Print or save as PDF")}
        >
          <Download className="h-4 w-4" />
          <span className="hidden sm:inline">PDF</span>
        </button>
      </div>

      <div
        id="written-article"
        className={open ? "mt-6" : "article-collapsed"}
        aria-hidden={open ? undefined : true}
      >
        {paragraphs.map((p, i) => (
          <div key={i}>
            <p
              data-no-translate
              className={`text-[17px] leading-[1.7] md:text-lg md:leading-[1.75] ${
                i === 0
                  ? "first-letter:float-left first-letter:mr-3 first-letter:mt-2 first-letter:font-display first-letter:text-[3.75rem] first-letter:uppercase first-letter:leading-[0.85] first-letter:text-primary md:first-letter:mr-4 md:first-letter:text-[5rem]"
                  : "mt-5 md:mt-6"
              }`}
            >
              {p}
            </p>
            {i === ctaIndex && explore && <InlineExploreCTA explore={explore} />}
            {i === imageIndex && midImageUrl && (
              <InlineMidImage src={midImageUrl} alt={midImageAlt ?? ""} />
            )}
          </div>
        ))}
        <NewsletterOutro />
      </div>
    </div>
  );
}



function InlineMidImage({ src, alt }: { src: string; alt: string }) {
  return (
    <figure className="my-8 overflow-hidden rounded-2xl border border-border bg-card">
      <div className="aspect-[16/10] w-full max-h-[420px]">
        <SmartImage src={src} alt={alt} className="h-full w-full object-cover" />
      </div>
    </figure>
  );
}

const OUTRO: Record<string, [string, string, string, string, string]> = {
  en: ["Keep the curiosity going", "If this made you a little more curious than you were a minute ago - good. That's the whole point. Join the ", "Smartasaurus Broadcast Channel", " on Instagram and get one impossible, true discovery whenever we find one.", "Join the Broadcast Channel"],
  fr: ["Restez curieux", "Si cette lecture a éveillé un peu plus votre curiosité qu'il y a une minute, tant mieux. C'est tout l'intérêt. Rejoignez la ", "chaîne de diffusion Smartasaurus", " sur Instagram et recevez une découverte incroyable, mais vraie, chaque fois que nous en trouvons une.", "Rejoindre la chaîne"],
  es: ["Que no pare la curiosidad", "Si esto te ha dejado un poco más curioso que hace un minuto, perfecto. De eso se trata. Únete al ", "canal de difusión de Smartasaurus", " en Instagram y recibe un descubrimiento increíble, pero real, cada vez que encontremos uno.", "Unirme al canal"],
  pt: ["Continue curioso", "Se isto deixou você um pouco mais curioso do que há um minuto, ótimo. É exatamente essa a ideia. Entre no ", "canal de transmissão do Smartasaurus", " no Instagram e receba uma descoberta incrível, mas verdadeira, sempre que encontrarmos uma.", "Entrar no canal"],
  de: ["Bleib neugierig", "Wenn dich das ein bisschen neugieriger gemacht hat als noch vor einer Minute – gut. Genau darum geht es. Folge dem ", "Smartasaurus-Broadcast-Kanal", " auf Instagram und erhalte eine unglaubliche, aber wahre Entdeckung, sobald wir eine finden.", "Dem Kanal beitreten"],
  sv: ["Fortsätt vara nyfiken", "Om det här gjorde dig lite mer nyfiken än för en minut sedan – bra. Det är hela poängen. Gå med i ", "Smartasaurus sändningskanal", " på Instagram och få en otrolig men sann upptäckt varje gång vi hittar en.", "Gå med i kanalen"],
  ru: ["Пусть любопытство не угасает", "Если после этого вам стало чуть любопытнее, чем минуту назад, — отлично. В этом весь смысл. Подпишитесь на ", "канал Smartasaurus", " в Instagram и получайте невероятное, но правдивое открытие каждый раз, когда мы его находим.", "Подписаться на канал"],
  zh: ["保持好奇", "如果这篇内容让你比一分钟前更好奇了一点——那就对了，这正是我们的初衷。欢迎加入 Instagram 上的", "Smartasaurus 广播频道", "，每当我们发现一个难以置信却真实的发现，都会第一时间分享给你。", "加入广播频道"],
  hi: ["जिज्ञासा बनाए रखें", "अगर इसे पढ़कर आपकी जिज्ञासा एक मिनट पहले से थोड़ी बढ़ी है, तो बढ़िया। यही तो मकसद है। Instagram पर ", "Smartasaurus ब्रॉडकास्ट चैनल", " से जुड़ें और जब भी हमें कोई अविश्वसनीय लेकिन सच्ची खोज मिले, वह सीधे आप तक पहुँचे।", "चैनल से जुड़ें"],
  ar: ["ابقَ فضوليًا", "إن جعلك هذا أكثر فضولًا قليلًا مما كنت عليه قبل دقيقة، فهذا رائع. هذا هو الهدف كله. انضم إلى ", "قناة Smartasaurus", " على Instagram لتصلك اكتشافات مذهلة وحقيقية كلما وجدنا واحدًا.", "انضم إلى القناة"],
};

function NewsletterOutro() {
  const [lang] = useLocale();
  const t = OUTRO[lang] ?? OUTRO.en!;
  return (
    <aside
      aria-label={t[2]}
      data-no-translate
      className="mt-10 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-primary/5 to-transparent p-5 md:p-6"
    >
      <div className="flex items-start gap-3">
        <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">{t[0]}</div>
          <p className="mt-2 text-[15px] leading-relaxed text-foreground md:text-base">
            {t[1]}<strong>{t[2]}</strong>{t[3]}
          </p>
          <a
            href="https://www.instagram.com/channel/Abb0uo2Ci9GzCQmM/"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-4 inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-xs font-bold uppercase tracking-wider text-primary-foreground transition hover:brightness-110"
          >
            {t[4]} <ArrowRight className="h-3.5 w-3.5 rtl:rotate-180" />
          </a>
        </div>
      </div>
    </aside>
  );
}

function InlineExploreCTA({ explore }: { explore: ExploreCTA }) {
  const linkProps =
    explore.href.to === "/discovery/$slug"
      ? { to: "/discovery/$slug" as const, params: explore.href.params }
      : { to: "/category/$slug" as const, params: explore.href.params };
  return (
    <Link
      {...linkProps}
      className="my-8 flex items-center gap-3 rounded-2xl border-l-4 border-primary bg-primary/5 p-4 transition hover:bg-primary/10 md:p-5"
    >
      <Compass className="h-5 w-5 shrink-0 text-primary" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.3em] text-primary">{explore.label}</div>
        <div className="mt-0.5 truncate font-display text-sm uppercase text-foreground md:text-base">
          {explore.title}
        </div>
      </div>
      <ArrowRight className="h-4 w-4 shrink-0 text-primary" />
    </Link>
  );
}
